/**
 * Generic labeled form-field renderer + form value reader.
 *
 * Used by every entity's edit modal. Produces an HTML string for use inside
 * `openModal({ body })`. The matching `readForm()` walks the form's
 * inputs/selects/textareas and returns a plain object keyed by `name`.
 */
import { escapeHtml } from "../lib/dom";

export interface FormFieldOptions {
  label: string;
  name: string;
  type?: string;          // input type (text, number, date, etc.); use "textarea" for multi-line
  value?: any;
  options?: Array<string | { value: string; label: string }> | null;   // when set, renders a <select> with these choices
  required?: boolean;
  hint?: string | null;
  placeholder?: string;
  step?: string | null;
  /** Upper bound for date/number inputs. Acquisition dates pass todayISO()
   *  here — a lot can't be acquired in the future, and a future acq_date
   *  corrupts holding period (short vs long term) and date-bounded reports. */
  max?: string | null;
}

export function formField({
  label, name,
  type = "text",
  value = "",
  options = null,
  required = false,
  hint = null,
  placeholder = "",
  step = null,
  max = null,
}: FormFieldOptions): string {
  const id = "f_" + name.replace(/\s+/g, "_");
  let inp: string;
  if (options) {
    inp = `<select class="select" id="${id}" name="${name}">
      <option value="">—</option>
      ${options.map(o => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return `<option value="${escapeHtml(v)}" ${String(value) === String(v) ? "selected" : ""}>${escapeHtml(l)}</option>`;
      }).join("")}
    </select>`;
  } else if (type === "textarea") {
    inp = `<textarea class="textarea" id="${id}" name="${name}" rows="3" placeholder="${escapeHtml(placeholder)}">${escapeHtml(value || "")}</textarea>`;
  } else {
    const stepAttr = step ? `step="${step}"` : (type === "number" ? 'step="0.01"' : "");
    const maxAttr = max ? `max="${escapeHtml(max)}"` : "";
    inp = `<input class="input" id="${id}" name="${name}" type="${type}" ${stepAttr} ${maxAttr} value="${escapeHtml(value == null ? "" : value)}" ${required ? "required" : ""} placeholder="${escapeHtml(placeholder)}">`;
  }
  return `<div><label class="label" for="${id}">${escapeHtml(label)}${required ? ' <span class="text-red-400">*</span>' : ""}</label>${inp}${hint ? `<div class="text-xs text-[var(--muted)] mt-1">${hint}</div>` : ""}</div>`;
}

/** Walk every named input/select/textarea inside a form and collect values. */
export function readForm(formEl: HTMLElement | null): Record<string, any> {
  const data: Record<string, any> = {};
  if (!formEl) return data;
  const fields = formEl.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "input, select, textarea"
  );
  fields.forEach((el) => {
    if (!el.name) return;
    let v: any = el.value;
    if ((el as HTMLInputElement).type === "number") v = v === "" ? null : Number(v);
    if ((el as HTMLInputElement).type === "checkbox") v = (el as HTMLInputElement).checked;
    data[el.name] = v;
  });
  return data;
}
